import { useCallback, useMemo } from 'react'

import {
  SurveyAnsweringForm,
  type SurveyFormBuilderElement,
  type SurveyAnswers,
  type SurveyAnswerValue,
} from '@factorialco/f0-react'
import type { ModuleId } from '@factorialco/f0-react/dist/experimental'

import DOMPurify from 'dompurify'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useDirectUpload } from 'modules/core/hooks/useDirectUpload'
import i18n from 'modules/core/lib/i18n'
import { FromQuestionnaireToForm } from 'modules/forms/components/newFormEditor/helpers'
import { useLoadForm, useLoadResponses } from 'modules/forms/hooks/useForms'
import {
  mapFormToQuestionnaire,
  mapFormAnswersToSurveyAnswers,
} from 'modules/forms/lib/surveyAnsweringFormHelpers'
import { usePurchaseRequestDatasets } from 'modules/procurement/components/purchaseRequests/PurchaseRequestForm/datasets'

import type { FormItem } from './formsTypes'

const PROCUREMENT_MODULE_ID: ModuleId = 'finance-spending'

interface Props {
  formItem: FormItem
  isOpen: boolean
  onClose: () => void
}

const FormContent = ({ formItem, isOpen, onClose }: Props) => {
  const { company } = useCurrentSession()
  const datasets = usePurchaseRequestDatasets(company.id)
  const directUpload = useDirectUpload({ resourceType: formItem.resourceType })

  const useUpload = useCallback(
    () => ({
      upload: async (file: File) => {
        const result = await directUpload.upload(file)
        if (result.type === 'aborted') return { type: 'aborted' as const }
        return { type: 'success' as const, value: result.signedId }
      },
      cancelUpload: directUpload.cancelUpload,
      progress: directUpload.progress,
      status: directUpload.status,
    }),
    [directUpload]
  )

  const { form: responsesForm, loadingForm: loadingResponses } = useLoadResponses({
    membershipId: formItem.membershipId,
    resourceType: formItem.resourceType,
    materializedFormResourceType: formItem.resourceType,
    formId: formItem.formId,
  })

  const { form: directForm, loadingForm: loadingDirect } = useLoadForm({
    formId: formItem.formId,
    resourceType: formItem.resourceType,
    skip: !!responsesForm,
  })

  const isLoading = loadingResponses || (!responsesForm && loadingDirect)

  const form = useMemo(() => {
    if (responsesForm) return responsesForm

    if (!directForm) return undefined
    for (const answer of formItem.answers) {
      const element = directForm.getElement(answer.elementId)
      if (element) {
        element.answer = {
          answer: answer.answer ?? '',
          comment: answer.comment ?? '',
          options: answer.elementOptionIds ?? [],
          isCorrect: typeof answer.isCorrect === 'boolean' ? answer.isCorrect : undefined,
        }
      }
    }
    return directForm
  }, [responsesForm, directForm, formItem.answers])

  const elements = useMemo<SurveyFormBuilderElement[]>(() => {
    if (!form) return []
    return FromQuestionnaireToForm(mapFormToQuestionnaire(form).elements, {
      mapDropdownSingleTo: 'dropdown-single',
    })
  }, [form])

  const defaultValues = useMemo<Partial<SurveyAnswers> | undefined>(() => {
    if (!form) return undefined

    const answers = mapFormAnswersToSurveyAnswers(form)

    const emptyValueForType = (type: string): SurveyAnswerValue => {
      switch (type) {
        case 'rating':
          return { type: 'rating', value: null }
        case 'numeric':
          return { type: 'numeric', value: null }
        case 'multi-select':
          return { type: 'multi-select', value: null }
        case 'date':
          return { type: 'date', value: null }
        case 'select':
          return { type: 'select', value: null }
        case 'dropdown-single':
          return { type: 'dropdown-single', value: null }
        case 'link':
          return { type: 'link', value: null }
        case 'checkbox':
          return { type: 'checkbox', value: null }
        case 'file':
          return { type: 'file', value: null }
        case 'longText':
          return { type: 'longText', value: null }
        default:
          return { type: 'text', value: null }
      }
    }

    for (const el of elements) {
      if (el.type === 'section') {
        for (const q of el.section.questions ?? []) {
          if (!(q.id in answers)) {
            answers[q.id] = emptyValueForType(q.type)
          }
        }
      } else if (!(el.question.id in answers)) {
        answers[el.question.id] = emptyValueForType(el.question.type)
      }
    }

    return answers
  }, [form, elements])

  return (
    <SurveyAnsweringForm
      elements={elements}
      datasets={datasets}
      mode="all-questions"
      title={form?.title ?? formItem.title}
      description={
        form?.description
          ? DOMPurify.sanitize(form.description, { USE_PROFILES: { html: false } })
          : undefined
      }
      defaultValues={defaultValues}
      preview
      useUpload={useUpload}
      isOpen={isOpen}
      onClose={onClose}
      loading={isLoading}
      position="right"
      module={{
        id: PROCUREMENT_MODULE_ID,
        label: i18n.t('sidebar.finance_spending'),
        href: '/procurement',
      }}
    />
  )
}

export default FormContent
